import {$, hide, show} from "./Dom.ts";
import {$post, getCsrfToken} from "./Ajax.ts";
import {
    createErrorNotification,
    createSuccessNotification,
    createWarningNotification
} from "./Notifications.ts";

export function keysMatch(appServerKeyBuffer: ArrayBuffer | null, expectedBase64: string): boolean {
    if (!appServerKeyBuffer || !expectedBase64) return false;
    const padding = '='.repeat((4 - expectedBase64.length % 4) % 4);
    const base64 = (expectedBase64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const keyBytes = new Uint8Array(appServerKeyBuffer);
    if (keyBytes.length !== rawData.length) return false;
    for (let i = 0; i < keyBytes.length; i++) {
        if (keyBytes[i] !== rawData.codePointAt(i)) return false;
    }
    return true;
}

document.addEventListener('DOMContentLoaded', () => {

    const subBtn = $<HTMLButtonElement>('#webpush-subscribe-button');
    const testBtn = $<HTMLButtonElement>('#webpush-test-button');
    const serviceWorker = $<HTMLMetaElement>('meta[name="service-worker-js"]');

    if (testBtn) {
        testBtn.addEventListener('click', handleTestClick);

        function handleTestClick() {
            testBtn.disabled = true;
            const originalHtml = testBtn.innerHTML;
            testBtn.innerText = 'Envoi en cours...';

            const csrfToken = getCsrfToken();
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');
            if (csrfToken) {
                headers.append('X-CSRFToken', csrfToken);
            }
            $post(testBtn.dataset.url!, {csrfmiddlewaretoken: csrfToken}, headers, {
                credentials: 'include'
            }).then(({body, response}) => {
                testBtn.disabled = false;
                testBtn.innerHTML = originalHtml;
                if (response.ok) {
                    createSuccessNotification({message: body?.message || "Notification push de test envoyée avec succès !"});
                } else {
                    if (body?.resubscribe_needed) {
                        window.isPushEnabled = false;
                        if (subBtn) {
                            subBtn.innerText = 'Abonnez-vous aux notifications Push';
                            subBtn.disabled = false;
                        }
                        hide(testBtn);
                        show($<HTMLElement>("#alert_notif_push"));
                        if ('serviceWorker' in navigator) {
                            navigator.serviceWorker.ready
                                .then(reg => reg.pushManager.getSubscription())
                                .then(sub => sub?.unsubscribe());
                        }
                    }
                    createErrorNotification({message: body?.message || "Erreur lors de l'envoi de la notification push de test."});
                }
            }).catch(() => {
                testBtn.disabled = false;
                testBtn.innerHTML = originalHtml;
                createErrorNotification({message: "Erreur réseau lors de l'envoi de la notification push de test."});
            });
        }
    }

    if (subBtn && serviceWorker) {
        const serviceWorkerPath = serviceWorker.content;
        subBtn.addEventListener('click', handleClick);

        async function handleClick() {
            subBtn.disabled = true;
            if (window.isPushEnabled) {
                try {
                    const registration = await navigator.serviceWorker.getRegistration(serviceWorkerPath);
                    if (registration) {
                        return unsubscribe(registration);
                    } else {
                        createErrorNotification({message: 'Impossible de vous désabonner, aucun service worker trouvé.'});
                        subBtn.disabled = false;
                        return;
                    }
                } catch {
                    createErrorNotification({message: 'Impossible de vous désabonner, une erreur est survenue.'});
                    subBtn.disabled = false;
                    return;
                }
            }

            // Do everything if the Browser Supports Service Worker
            if ('serviceWorker' in navigator) {
                subBtn.innerText = 'Chargement en cours....';
                navigator.serviceWorker.register(serviceWorkerPath).then((reg: ServiceWorkerRegistration) => {
                    initialiseState(reg);
                }).catch((error) => {
                    console.error('ServiceWorker registration error:', error);
                    createErrorNotification({message: "Impossible d'enregistrer le service worker."});
                    subBtn.innerText = 'Abonnez-vous aux notifications Push';
                    subBtn.disabled = false;
                });
            } else {
                createWarningNotification({message: 'Les notifications ne sont pas supportées par votre navigateur !'});
                subBtn.disabled = true;
            }
        }

        async function initialiseState(reg: ServiceWorkerRegistration) {
            // Are Notifications supported in the service worker?
            if (!(reg.showNotification)) {
                createWarningNotification({message: 'Les notifications ne sont pas supportées par votre navigateur !'});
                subBtn.innerText = 'Abonnez-vous aux notifications Push';
                subBtn.disabled = true;
                return;
            }

            // Check if push messaging is supported
            if (!('PushManager' in window)) {
                createWarningNotification({message: 'Les notifications ne sont pas supportées par votre navigateur !'});
                subBtn.innerText = 'Abonnez-vous aux notifications Push';
                subBtn.disabled = true;
                return;
            }

            // Check the current Notification permission.
            if (Notification.permission === 'denied') {
                createWarningNotification({message: 'Votre navigateur bloque les notifications Push ! Veuillez modifier les autorisations de votre navigateur.'});
                subBtn.innerText = 'Abonnez-vous aux notifications Push';
                subBtn.disabled = false;
                return;
            }

            // Explicitly request permission if in default state
            if (Notification.permission === 'default') {
                try {
                    const permission = await Notification.requestPermission();
                    if (permission !== 'granted') {
                        createWarningNotification({message: 'Vous devez autoriser les notifications pour vous abonner.'});
                        subBtn.innerText = 'Abonnez-vous aux notifications Push';
                        subBtn.disabled = false;
                        return;
                    }
                } catch {
                    createErrorNotification({message: "Erreur lors de la demande d'autorisation de notifications."});
                    subBtn.innerText = 'Abonnez-vous aux notifications Push';
                    subBtn.disabled = false;
                    return;
                }
            }

            // We need to subscribe for push notification and send the information to server
            subscribe(reg);
        }

        function subscribe(reg: ServiceWorkerRegistration) {
            // Get the Subscription or register one
            getSubscription(reg).then((subscription: PushSubscription) => {
                postSubscribeObj(reg, 'subscribe', subscription);
            }).catch((error: any) => {
                console.error('Subscription error.', error);
                createErrorNotification({message: "Impossible de s'abonner aux notifications Push."});
                subBtn.innerText = 'Abonnez-vous aux notifications Push';
                subBtn.disabled = false;
            });
        }

        function urlB64ToUint8Array(base64String: string) {
            const padding = '='.repeat((4 - base64String.length % 4) % 4);
            const base64 = (base64String + padding)
                .replace(/-/g, '+')
                .replace(/_/g, '/');

            const rawData = window.atob(base64);
            const outputArray = new Uint8Array(rawData.length);

            for (let i = 0; i < rawData.length; ++i) {
                outputArray[i] = rawData.codePointAt(i) ?? 0;
            }
            return outputArray;
        }

        async function getSubscription(reg: ServiceWorkerRegistration) {
            const metaObj = $<HTMLMetaElement>('meta[name="django-webpush-vapid-key"]');
            if (!metaObj || !metaObj.content) {
                throw new Error("Clé VAPID introuvable");
            }
            const applicationServerKey = metaObj.content;
            const subscription = await reg.pushManager.getSubscription();
            if (subscription) {
                if (subscription.options && subscription.options.applicationServerKey) {
                    if (!keysMatch(subscription.options.applicationServerKey, applicationServerKey)) {
                        console.warn("Souscription Push avec une ancienne clé VAPID détectée. Réinitialisation...");
                        await subscription.unsubscribe();
                    } else {
                        return subscription;
                    }
                } else {
                    await subscription.unsubscribe();
                }
            }
            const options: PushSubscriptionOptionsInit = {
                userVisibleOnly: true,
                applicationServerKey: urlB64ToUint8Array(applicationServerKey)
            };
            return reg.pushManager.subscribe(options);
        }

        function unsubscribe(reg: ServiceWorkerRegistration) {
            // Get the Subscription to unregister
            reg.pushManager.getSubscription().then((subscription: PushSubscription | null) => {
                if (!subscription) {
                    subBtn.disabled = false;
                    subBtn.innerText = 'Abonnez-vous aux notifications Push';
                    window.isPushEnabled = false;
                    createErrorNotification({message: 'Aucun abonnement actif trouvé.'});
                    return;
                }
                postSubscribeObj(reg, 'unsubscribe', subscription);
            }).catch(() => {
                subBtn.disabled = false;
                subBtn.innerText = 'Se désabonner des notifications Push';
                createErrorNotification({message: 'Erreur lors de la récupération de la souscription.'});
            });
        }

        function postSubscribeObj(registration: ServiceWorkerRegistration, statusType: string, subscription: PushSubscription) {
            const browserMatches = navigator.userAgent.match(/(firefox|msie|chrome|safari|trident|edg)/ig);
            const browser = browserMatches ? browserMatches[0].toLowerCase() : 'unknown';
            const user_agent = navigator.userAgent;
            const data = {
                status_type: statusType,
                subscription: subscription.toJSON(),
                browser: browser,
                user_agent: user_agent,
                group: subBtn.dataset.group
            };

            const csrfToken = getCsrfToken();
            const headers = new Headers();
            headers.append('Content-Type', 'application/json');
            if (csrfToken) {
                headers.append('X-CSRFToken', csrfToken);
            }
            $post(subBtn.dataset.url!, data, headers, {
                credentials: 'include'
            }).then(({response}) => {
                if ((response.status == 201) && (statusType == 'subscribe')) {
                    // Show unsubscribe button instead
                    subBtn.innerText = 'Se désabonner des notifications Push';
                    subBtn.disabled = false;
                    window.isPushEnabled = true;
                    createSuccessNotification({message: 'Vous avez activé les notifications Push.'});
                    hide($<HTMLElement>("#alert_notif_push"));
                    if (testBtn) show(testBtn);
                } else if ((response.status == 202) && (statusType == 'unsubscribe')) {
                    // Remove the subscription from browser
                    subscription.unsubscribe().then(() => {
                        subBtn.innerText = 'Abonnez-vous aux notifications Push';
                        createSuccessNotification({message: 'Vous vous êtes bien désabonné des notifications Push.'});
                        window.isPushEnabled = false;
                        subBtn.disabled = false;
                        show($<HTMLElement>("#alert_notif_push"));
                        if (testBtn) hide(testBtn);
                    }).catch(() => {
                        createErrorNotification({message: 'Erreur lors de la résiliation de la souscription locale.'});
                        subBtn.innerText = 'Abonnez-vous aux notifications Push';
                        subBtn.disabled = false;
                        window.isPushEnabled = false;
                    });
                } else {
                    createErrorNotification({message: 'Erreur dans le traitement de la demande par le serveur.'});
                    subBtn.innerText = window.isPushEnabled ? 'Se désabonner des notifications Push' : 'Abonnez-vous aux notifications Push';
                    subBtn.disabled = false;
                }
            }).catch(() => {
                createErrorNotification({message: 'Erreur réseau lors de la communication avec le serveur.'});
                subBtn.innerText = window.isPushEnabled ? 'Se désabonner des notifications Push' : 'Abonnez-vous aux notifications Push';
                subBtn.disabled = false;
            });
        }
    }
});
